import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loadCSS, removeCSS } from "../utils/domAssets.js";
import toast from "../utils/toast.js";
import { setCookie } from "../utils/cookies.js";
import { login } from "../services/authService.js";

export default function LoginFleetOwner() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadStyles() {
      await Promise.all([
        loadCSS(
          "https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap",
          "website-fonts",
        ),
        loadCSS("/assets/website/css/style.css", "website-css"),
        loadCSS("/assets/website/css/login.css", "login-css"),
      ]);

      if (isMounted) {
        setIsLoading(false);
      }
    }

    loadStyles();

    return () => {
      isMounted = false;
      removeCSS("website-fonts");
      removeCSS("website-css");
      removeCSS("login-css");
    };
  }, []);

  function validate() {
    setMobileError("");
    setPasswordError("");
    let valid = true;
    if (!/^\d{10}$/.test(mobile.trim())) {
      setMobileError("Enter a valid 10-digit mobile number.");
      valid = false;
    }
    if (!password) {
      setPasswordError("Please enter your password.");
      valid = false;
    }
    return valid;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await login({ mobile: mobile.trim(), password });

      if (!res.success) {
        toast.error(res.message || "Login failed. Please try again.");
        return;
      }

      if (res.data.role !== "owner") {
        toast.error(
          "This number is registered as a Customer. Use the Customer login instead.",
        );
        return;
      }

      setCookie("token", res.token);
      setCookie("user", JSON.stringify(res.data));
      toast.success(`Welcome back, ${res.data.name}!`);
      navigate("/panel");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Login failed. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="website-loader">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="login-body">
      <img
        src="/assets/website/images/pattern-dots.svg"
        className="login-pattern"
        alt=""
        aria-hidden="true"
      />
      <div className="login-shell">
        <div className="login-card">
          <Link to="/" className="brand login-brand">
            <img
              src="/assets/website/images/logo.svg"
              alt="RouteRider logo"
              className="brand-logo"
            />
            <span className="brand-name brand-name-light light">
              Route<span className="accent-text">Rider</span>
            </span>
          </Link>
          <p className="auth-sub">Log in to manage your fleet.</p>
          <form onSubmit={handleSubmit} noValidate>
            <div className="field">
              <label htmlFor="liMobile"> mobile number</label>
              <input
                type="tel"
                id="liMobile"
                name="mobile"
                placeholder="10-digit mobile number"
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel"
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
              />
              <p className="field-error">{mobileError}</p>
            </div>
            <div className="field">
              <label htmlFor="liPassword">Password</label>
              <div className="password-wrap">
                <input
                  type={showPassword ? "text" : "password"}
                  id="liPassword"
                  name="password"
                  placeholder="Your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="pw-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
              <p className="field-error">{passwordError}</p>
            </div>
            <div className="login-row">
              <label className="checkbox-row sm">
                <input type="checkbox" id="liRemember" />
                <span>Remember me</span>
              </label>
              <a href="#" className="forgot-link">
                Forgot password?
              </a>
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={submitting}
            >
              {submitting ? "Logging in..." : "Log in →"}
            </button>
          </form>
          <p className="auth-switch">
            New to RouteRider? <Link to="/signup?role=owner">Create an account</Link>
          </p>
          <p className="auth-switch">
            <Link to="/">← Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
